package dev.youndie.site.components.layouts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.data.addIfAbsent
import com.varabyte.kobweb.core.data.getValue
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import dev.youndie.site.components.sections.SiteFooter
import dev.youndie.site.components.sections.SiteNav
import dev.youndie.site.model.AUTHOR_NAME
import dev.youndie.site.model.SITE_NAME
import dev.youndie.site.model.SITE_URL
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Main

/**
 * @property title what goes in `<title>` and the browser tab. This is the line a
 *   search result shows, so it should carry the words somebody would type — the
 *   headline on the page can be the literary one.
 * @property description one sentence; used for `<meta name="description">` and the
 *   social card. Pages without one are invisible to search engines, so provide it.
 * @property socialTitle the headline for link previews, when the tab title is too
 *   terse to stand on its own. Defaults to [title].
 * @property noIndex keeps the page out of search results — for the error page,
 *   which otherwise gets indexed once per broken inbound link.
 * @property canonicalUrl points somewhere else entirely. Set it when a page also
 *   exists on another site — a post first published elsewhere — so search engines
 *   credit the original instead of treating the two as duplicates and ranking
 *   neither. Defaults to this page's own address.
 * @property image the link-preview picture, as a site-absolute path. Defaults to the
 *   site-wide card declared in the build script — which is right for the home and
 *   about pages and wrong for a post, where every share would look identical.
 * @property publishedTime an ISO date. Its presence is what makes the page an
 *   `article` rather than a `website` to everything that reads Open Graph.
 * @property jsonLd one schema.org object, already serialised. Search engines read
 *   this instead of guessing what the page is from its markup.
 * @property hasMarkdownCopy the build writes this page's text next to it as `<path>.md`
 *   (library pages and posts), so the head can point an agent at it. Only pages built
 *   from markdown have one; claiming it anywhere else is a link to a 404.
 */
class PageLayoutData(
    val title: String,
    val description: String? = null,
    val socialTitle: String? = null,
    val noIndex: Boolean = false,
    val canonicalUrl: String? = null,
    val image: String? = null,
    val publishedTime: String? = null,
    val jsonLd: String? = null,
    val hasMarkdownCopy: Boolean = false,
)

@InitRoute
fun initPageLayout(ctx: InitRouteContext) {
    ctx.data.addIfAbsent {
        console.warn("${ctx.route.path} did not provide PageLayoutData")
        PageLayoutData(SITE_NAME)
    }
}

/**
 * Creates the `<head>` element the first time and updates it afterwards.
 *
 * Two things depend on this: client-side navigation, which has to rewrite the tags
 * rather than reload the page, and `kobweb export`, which snapshots the DOM after
 * the page renders — so whatever lands here also lands in the exported HTML that
 * crawlers and link unfurlers read.
 */
private fun upsertMeta(attribute: String, key: String, content: String) {
    val head = document.head ?: return
    val meta = head.querySelector("meta[$attribute='$key']")
        ?: document.createElement("meta").also {
            it.setAttribute(attribute, key)
            head.appendChild(it)
        }
    meta.setAttribute("content", content)
}

/** Removes a tag a previous route added, so navigating away clears what no longer applies. */
private fun dropMeta(attribute: String, key: String) {
    document.head?.querySelector("meta[$attribute='$key']")?.remove()
}

private fun upsertCanonical(href: String) {
    val head = document.head ?: return
    val link = head.querySelector("link[rel='canonical']")
        ?: document.createElement("link").also {
            it.setAttribute("rel", "canonical")
            head.appendChild(it)
        }
    link.setAttribute("href", href)
}

/**
 * `<link rel="alternate" type="text/markdown">` to the page's text, or none. Removed
 * rather than left alone on a page without a copy: client-side navigation from a post
 * to the home page would otherwise keep pointing at the post's text.
 */
private fun upsertMarkdownAlternate(href: String?) {
    val head = document.head ?: return
    val existing = head.querySelector("link[rel='alternate'][type='text/markdown']")
    if (href == null) {
        existing?.let { head.removeChild(it) }
        return
    }
    val link = existing ?: document.createElement("link").also {
        it.setAttribute("rel", "alternate")
        it.setAttribute("type", "text/markdown")
        head.appendChild(it)
    }
    link.setAttribute("href", href)
}

/**
 * One structured-data block per page, replaced rather than appended — client-side
 * navigation would otherwise leave the previous page's object behind and describe
 * the document as two different things at once.
 */
private fun upsertJsonLd(json: String?) {
    val head = document.head ?: return
    val existing = head.querySelector("script[type='application/ld+json']")
    if (json == null) {
        existing?.remove()
        return
    }
    val script = existing
        ?: document.createElement("script").also {
            it.setAttribute("type", "application/ld+json")
            head.appendChild(it)
        }
    script.textContent = json
}

@Layout
@Composable
fun PageLayout(ctx: PageContext, content: @Composable () -> Unit) {
    val data = ctx.data.getValue<PageLayoutData>()
    val path = ctx.route.path

    LaunchedEffect(
        data.title, data.description, data.socialTitle, data.noIndex,
        data.canonicalUrl, data.image, data.publishedTime, data.jsonLd, data.hasMarkdownCopy, path,
    ) {
        document.title = data.title

        // Written unconditionally: navigating away from the error page has to clear
        // the noindex it set, not inherit it.
        upsertMeta("name", "robots", if (data.noIndex) "noindex, follow" else "index, follow")

        // Trailing slashes would otherwise give every page two canonical spellings.
        val ownUrl = SITE_URL + path.trimEnd('/').ifEmpty { "/" }
        upsertCanonical(data.canonicalUrl ?: ownUrl)
        upsertMarkdownAlternate(if (data.hasMarkdownCopy) "$ownUrl.md" else null)
        // og:url stays this page even when the canonical points elsewhere: a share of
        // this link should unfurl as this link.
        upsertMeta("property", "og:url", ownUrl)

        val socialTitle = data.socialTitle ?: data.title
        upsertMeta("property", "og:title", socialTitle)
        upsertMeta("name", "twitter:title", socialTitle)

        data.description?.let { description ->
            upsertMeta("name", "description", description)
            upsertMeta("property", "og:description", description)
            upsertMeta("name", "twitter:description", description)
        }

        // A dated page is an article; everything else is the site itself. Both branches
        // write, because a stale `article:published_time` left over from the previous
        // route would date this page to somebody else's day.
        if (data.publishedTime != null) {
            upsertMeta("property", "og:type", "article")
            upsertMeta("property", "article:published_time", data.publishedTime)
            upsertMeta("property", "article:author", AUTHOR_NAME)
        } else {
            upsertMeta("property", "og:type", "website")
            dropMeta("property", "article:published_time")
            dropMeta("property", "article:author")
        }

        upsertJsonLd(data.jsonLd)

        // Absolute, because Open Graph rejects a relative path — a card that never
        // renders looks the same as no card at all. Pages that set nothing keep the
        // site-wide image already in the document from the build script.
        data.image?.let { image ->
            val absolute = if (image.startsWith("http")) image else SITE_URL + image
            upsertMeta("property", "og:image", absolute)
            upsertMeta("name", "twitter:image", absolute)
        }
    }

    Div(attrs = { classes("site-root") }) {
        SiteNav(ctx)
        Main(attrs = { classes("site-main") }) {
            content()
        }
        SiteFooter()
    }
}
